import React, { useContext, useEffect, useRef, useState } from 'react';
import { Modal } from '../Common/Modal';
import Alert from '../Common/Alert';
import ElectronicSign from '../Common/ElectronicSign';
import api from '../../services/api';
import visitService from '../../services/visitService';
import { VISIT_TYPES, formatVisitId } from '../../utils/constants';
import { visits as visitMessages, common as commonMessages } from '../../utils/validationMessages';
import { formatDateDDMMMYYYY } from '../../utils/dateFormat';
import letterTemplateService from '../../services/letterTemplateService';
import { buildLetterPartsFromTemplate } from '../../utils/letterTemplateBuilder';
import { downloadVisitLetterPdfFromHtml, getVisitLetterPdfErrorMessage } from '../../utils/visitLetterPdf';
import { extractReportCreatedByBlock } from '../../utils/visitLetterDisplay';
import { formatActionItemStatusForDisplay } from '../../utils/actionItemUi';
import { AuthContext } from '../../context/AuthContext';
import VisitModalHeader from './VisitModalHeader';
import './GenerateVisitLetterModal.css';

const getBodyPlainText = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
};

// Markers used to delimit the auto-appended Issues (Open / Resolved) table inside
// LetterBody so we can strip + regenerate it on every load/submit and avoid duplicates.
const OPEN_ISSUES_MARKER_START = '<!--CTMS_OPEN_ISSUES_TABLE_START-->';
const OPEN_ISSUES_MARKER_END = '<!--CTMS_OPEN_ISSUES_TABLE_END-->';

const stripOpenIssuesBlock = (html) => {
  if (!html) return '';
  const startIdx = html.indexOf(OPEN_ISSUES_MARKER_START);
  if (startIdx === -1) return html;
  const endIdx = html.indexOf(OPEN_ISSUES_MARKER_END, startIdx);
  if (endIdx === -1) return html.slice(0, startIdx).trimEnd();
  return (html.slice(0, startIdx) + html.slice(endIdx + OPEN_ISSUES_MARKER_END.length)).trimEnd();
};

const escapeHtml = (value) => {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

// Builds an email-safe (inline-styled) HTML version of the Issues table (all statuses including Closed)
// appended to LetterBody so it appears in the outgoing follow-up email.
const buildOpenIssuesTableHtml = (issues, visit) => {
  const rows = (Array.isArray(issues) ? issues : []);
  const fallbackSite = visit?.siteCode ?? visit?.SiteCode ?? '';
  const cellStyle = 'border:1px solid #d1d5db;padding:7px 8px;text-align:left;vertical-align:top;font-size:12px;color:#1f2937;word-break:break-word;';
  const headStyle = `${cellStyle}background-color:#eef2f6;color:#134252;font-weight:600;`;
  // Match Generate Preview table columns (8 cols — no Visit Type / Assigned To).
  const headers = ['Reference No', 'Site', 'Visit Date', 'Field Name', 'Issue Description', 'Expected Closure Date', 'Priority', 'Current Status'];
  const headerHtml = headers.map((h) => `<th style="${headStyle}">${escapeHtml(h)}</th>`).join('');
  const bodyHtml = rows.length === 0
    ? `<tr><td colspan="8" style="${cellStyle}text-align:center;color:#6b7280;">No issues for this site.</td></tr>`
    : rows.map((item) => {
        const siteCell = [item.siteCode, item.siteDescription].filter(Boolean).join(' \u2013 ') || fallbackSite || '-';
        const vd = item.visitDate ?? item.VisitDate ?? item.plannedStartDate;
        return `<tr>` +
          `<td style="${cellStyle}">${escapeHtml(item.actionItemNumber ?? item.vActionItemNumber ?? '-')}</td>` +
          `<td style="${cellStyle}">${escapeHtml(siteCell)}</td>` +
          `<td style="${cellStyle}">${escapeHtml(vd ? formatDateDDMMMYYYY(vd) : '-')}</td>` +
          `<td style="${cellStyle}">${escapeHtml(item.actionItemOn || '-')}</td>` +
          `<td style="${cellStyle}">${escapeHtml(item.findingDescription || '-')}</td>` +
          `<td style="${cellStyle}">${escapeHtml(item.dueDate ? formatDateDDMMMYYYY(item.dueDate) : '-')}</td>` +
          `<td style="${cellStyle}">${escapeHtml(item.priorityDescription ?? item.severity ?? '-')}</td>` +
          `<td style="${cellStyle}">${escapeHtml(formatActionItemStatusForDisplay(item.status))}</td>` +
          `</tr>`;
      }).join('');
  return `${OPEN_ISSUES_MARKER_START}` +
    `<div style="margin-top:18px;margin-bottom:24px;border-top:1px solid #d1d5db;padding-top:14px;padding-bottom:8px;">` +
      `<h3 style="margin:0 0 10px;font-size:16px;font-weight:600;color:#134252;">Issues</h3>` +
      `<table style="width:100%;border-collapse:collapse;table-layout:auto;font-size:12px;">` +
        `<thead><tr>${headerHtml}</tr></thead>` +
        `<tbody>${bodyHtml}</tbody>` +
      `</table>` +
    `</div>` +
    `${OPEN_ISSUES_MARKER_END}`;
};

const REPORT_CREATED_MARKER_START = '<!--CTMS_REPORT_CREATED_BY_START-->';
const REPORT_CREATED_MARKER_END = '<!--CTMS_REPORT_CREATED_BY_END-->';

const stripReportCreatedByBlock = (html) => {
  if (!html) return '';
  const startIdx = html.indexOf(REPORT_CREATED_MARKER_START);
  if (startIdx === -1) return html;
  const endIdx = html.indexOf(REPORT_CREATED_MARKER_END, startIdx);
  if (endIdx === -1) return html.slice(0, startIdx).trimEnd();
  return (html.slice(0, startIdx) + html.slice(endIdx + REPORT_CREATED_MARKER_END.length)).trimEnd();
};

const getSignerFullName = (user) => {
  if (!user || typeof user !== 'object') return '';
  const fn = (user.firstName ?? user.FirstName ?? user.vFirstName ?? '').toString().trim();
  const ln = (user.lastName ?? user.LastName ?? user.vLastName ?? '').toString().trim();
  return [fn, ln].filter(Boolean).join(' ').trim();
};

const getSignerRole = (user) => {
  if (!user || typeof user !== 'object') return '';
  return (user.profileCode ?? user.ProfileCode ?? user.vUserTypeCode ?? user.role ?? user.Role ?? '').toString().trim();
};

const formatDateTimeWithSeconds = () => {
  const d = new Date();
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = String(d.getDate()).padStart(2, '0');
  const month = MONTHS[d.getMonth()].toUpperCase();
  const year = d.getFullYear();
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return { datePart: `${day}-${month}-${year}`, timePart: `${h}:${m}` };
};

const parseVRecordedSign = (signedBy) => {
  if (!signedBy || typeof signedBy !== 'string') return { name: '', role: '' };
  const s = signedBy.trim();
  if (!s) return { name: '', role: '' };
  const firstParen = s.indexOf('(');
  const name = firstParen > 0 ? s.substring(0, firstParen).trim() : s;
  const matches = s.match(/\(([^)]*)\)/g);
  const role = matches && matches.length > 0
    ? matches[matches.length - 1].replace(/[()]/g, '').trim()
    : '';
  return { name, role };
};

const formatUtcWithOffsetToDateTimeParts = (utcDate, offsetStr) => {
  if (utcDate == null) return null;
  const d = new Date(utcDate);
  if (Number.isNaN(d.getTime())) return null;
  let offsetMinutes = 0;
  if (offsetStr) {
    const m = String(offsetStr).trim().match(/^([+-]?)(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (m) {
      offsetMinutes = (parseInt(m[2], 10) * 60 + parseInt(m[3], 10)) * (m[1] === '-' ? -1 : 1);
    }
  }
  const local = new Date(d.getTime() + offsetMinutes * 60 * 1000);
  const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const day = String(local.getUTCDate()).padStart(2, '0');
  const month = MONTHS[local.getUTCMonth()];
  const year = local.getUTCFullYear();
  const h = String(local.getUTCHours()).padStart(2, '0');
  const mi = String(local.getUTCMinutes()).padStart(2, '0');
  return { datePart: `${day}-${month}-${year}`, timePart: `${h}:${mi}` };
};

const buildReportCreatedByHtml = (user) => {
  const name = getSignerFullName(user) || '\u00A0';
  const role = getSignerRole(user) || '\u00A0';
  const { datePart, timePart } = formatDateTimeWithSeconds();
  const cellStyle = 'border:1px solid #999;padding:7px 10px;text-align:left;vertical-align:middle;font-size:12px;';
  const labelStyle = `${cellStyle}font-weight:600;color:#134252;width:12%;white-space:nowrap;`;
  const headStyle = `${cellStyle}background-color:#eef2f6;color:#134252;font-weight:700;text-align:left;`;
  return `${REPORT_CREATED_MARKER_START}` +
    `<div style="margin-top:24px;border-top:1px solid #d1d5db;padding-top:14px;">` +
      `<table style="width:100%;border-collapse:collapse;table-layout:auto;font-size:12px;margin-bottom:16px;">` +
        `<thead><tr><th colspan="4" style="${headStyle}">Letter Generated By</th></tr></thead>` +
        `<tbody>` +
          `<tr>` +
            `<td style="${labelStyle}">Signature</td>` +
            `<td style="${cellStyle}">${escapeHtml(name)}</td>` +
            `<td style="${labelStyle}">Role in the Study</td>` +
            `<td style="${cellStyle}">${escapeHtml(role)}</td>` +
          `</tr>` +
          `<tr>` +
            `<td style="${labelStyle}">Name</td>` +
            `<td style="${cellStyle}">${escapeHtml(name)}</td>` +
            `<td style="${labelStyle}">Date</td>` +
            `<td style="${cellStyle}">${escapeHtml(datePart)} ${escapeHtml(timePart)}</td>` +
          `</tr>` +
        `</tbody>` +
      `</table>` +
      `<p style="font-size:11px;color:#555;margin-top:0;margin-bottom:16px;padding:0 2px;">` +
        `<strong>Note:</strong> This is electronically signed document and manual signature is not required.` +
      `</p>` +
    `</div>` +
    `${REPORT_CREATED_MARKER_END}`;
};

const GenerateFollowUpLetterModal = ({ isOpen, onClose, visit, resolvedPiName, onGenerated, mode = 'edit' }) => {
  const { user: authUser } = useContext(AuthContext);
  const [editorContent, setEditorContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [letterBodyError, setLetterBodyError] = useState('');
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const bodyEditorRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [blockFormat, setBlockFormat] = useState('p');
  const [fontFamily, setFontFamily] = useState('Arial');
  const [showPreview, setShowPreview] = useState(false);
  const [showESignModal, setShowESignModal] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [toast, setToast] = useState(null);
  const previewContentRef = useRef(null);
  const bodyContentInitializedRef = useRef(false);
  const eSignRemarksRef = useRef('');
  const [openIssues, setOpenIssues] = useState([]);
  const [openIssuesLoading, setOpenIssuesLoading] = useState(false);
  const [storedReportCreatedByHtml, setStoredReportCreatedByHtml] = useState('');
  const [backendCreatorData, setBackendCreatorData] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setHeaderText('');
      setEditorContent('');
      setFooterText('');
      setSaving(false);
      setShowPreview(false);
      setShowESignModal(false);
      setPdfDownloading(false);
      setHistory([]);
      setHistoryIndex(-1);
      setBlockFormat('p');
      setFontFamily('Arial');
      setLetterBodyError('');
      setOpenIssues([]);
      setOpenIssuesLoading(false);
      setStoredReportCreatedByHtml('');
      setBackendCreatorData(null);
      bodyContentInitializedRef.current = false;
      eSignRemarksRef.current = '';
      return;
    }
    if (!visit?.scheduledVisitID && !visit?.scheduledVisitId) return;

    const visitId = visit.scheduledVisitID ?? visit.scheduledVisitId;
    let cancelled = false;

    const loadProjectTemplate = async (detail) => {
      const projectCode = (detail?.projectCode ?? detail?.ProjectCode ?? visit?.projectCode ?? visit?.ProjectCode ?? '').toString().trim();
      if (!projectCode) {
        setLetterBodyError(visitMessages.followUpLetterTemplateNotConfigured);
        setHeaderText('');
        setEditorContent('');
        setFooterText('');
        setHistory([]);
        setHistoryIndex(-1);
        return;
      }
      try {
        const template = await letterTemplateService.getTemplate(projectCode, 'followup');
        const parts = buildLetterPartsFromTemplate(template, detail, authUser);
        setLetterBodyError('');
        setHeaderText(parts.headerText);
        setEditorContent(parts.bodyHtml);
        setFooterText(parts.footerText);
        setHistory([parts.bodyHtml]);
        setHistoryIndex(0);
      } catch (err) {
        const msg = err?.response?.data?.error ?? visitMessages.followUpLetterTemplateNotConfigured;
        setLetterBodyError(msg);
        setHeaderText('');
        setEditorContent('');
        setFooterText('');
        setHistory([]);
        setHistoryIndex(-1);
      }
    };

    visitService.getById(visitId).then(async (detail) => {
      if (cancelled) return;
      const savedBody = (detail.followUpLetterBody != null && String(detail.followUpLetterBody).trim() !== '') ? detail.followUpLetterBody : null;
      if (savedBody) {
        const rawFooter = detail.followUpLetterFooter ?? '';
        setStoredReportCreatedByHtml(
          extractReportCreatedByBlock(detail.followUpLetterBody) || extractReportCreatedByBlock(rawFooter)
        );
        setLetterBodyError('');
        const cleanedBody = stripReportCreatedByBlock(stripOpenIssuesBlock(detail.followUpLetterBody));
        setHeaderText(detail.followUpLetterHeader ?? '');
        setEditorContent(cleanedBody);
        setFooterText(stripReportCreatedByBlock(rawFooter));
        setHistory([cleanedBody]);
        setHistoryIndex(0);
      } else {
        await loadProjectTemplate(detail);
      }
      if (detail.followUpLetterCreatedBySign) {
        setBackendCreatorData({
          sign: detail.followUpLetterCreatedBySign,
          utc: detail.followUpLetterCreatedOnUtc,
          offset: detail.followUpLetterCreatedAtOffset,
        });
      }
      if (!cancelled) {
        setBlockFormat('p');
        setFontFamily('Arial');
        bodyContentInitializedRef.current = false;
      }
    }).catch((err) => {
      if (!cancelled) {
        console.error('Failed to load visit for follow-up letter', err);
        setLetterBodyError(err?.response?.data?.error ?? 'Failed to load visit for follow-up letter.');
        setHeaderText('');
        setEditorContent('');
        setFooterText('');
        setHistory([]);
        setHistoryIndex(-1);
        setBlockFormat('p');
        setFontFamily('Arial');
        bodyContentInitializedRef.current = false;
      }
    });

    return () => { cancelled = true; };
  }, [isOpen, visit, authUser]);

  useEffect(() => {
    if (isOpen && visit && bodyEditorRef.current && !bodyContentInitializedRef.current) {
      bodyEditorRef.current.innerHTML = editorContent;
      if (editorContent) bodyContentInitializedRef.current = true;
    }
  }, [isOpen, visit, editorContent]);

  useEffect(() => {
    if (!isOpen || !visit) {
      return undefined;
    }
    const siteIdRaw = visit.siteID ?? visit.SiteID ?? visit.siteId;
    const siteId = siteIdRaw != null ? Number(siteIdRaw) : null;
    const projectCode = (visit.projectCode ?? visit.ProjectCode ?? '').toString().trim();
    if (!siteId || siteId <= 0 || !projectCode) {
      setOpenIssues([]);
      setOpenIssuesLoading(false);
      return undefined;
    }
    let cancelled = false;
    setOpenIssuesLoading(true);
    (async () => {
      try {
        const scheduledVisitRaw = visit.scheduledVisitID ?? visit.ScheduledVisitID;
        const scheduledVisitNum = scheduledVisitRaw != null ? Number(scheduledVisitRaw) : NaN;
        const params = { siteId, projectCode };
        if (Number.isFinite(scheduledVisitNum) && scheduledVisitNum > 0) {
          params.scheduledVisitId = scheduledVisitNum;
        }
        const res = await api.get('/crf/visit-report-site-appendix', {
          params,
        });
        if (cancelled) return;
        const data = res?.data ?? {};
        setOpenIssues(Array.isArray(data.openIssues) ? data.openIssues : []);
      } catch (_err) {
        if (!cancelled) setOpenIssues([]);
      } finally {
        if (!cancelled) setOpenIssuesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, visit]);

  if (!visit) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Generate Follow-Up Letter">
        <p>No visit selected.</p>
      </Modal>
    );
  }

  const isViewMode = String(mode || 'edit').toLowerCase() === 'view';

  const doSendLetter = async (vRemarks, options = {}) => {
    const { closeAfter = true, isDraft = false } = options;
    let cleanedBody = stripOpenIssuesBlock(editorContent || '');
    cleanedBody = stripReportCreatedByBlock(cleanedBody);
    const issuesHtml = buildOpenIssuesTableHtml(openIssues, visit);
    const reportCreatedByHtml = buildReportCreatedByHtml(authUser);
    // Match Preview order: body → issues → footer → Letter Generated By
    const letterBodyForSend = `${cleanedBody}${issuesHtml}`;
    const cleanedFooter = stripReportCreatedByBlock(footerText || '');
    const letterFooterForSend = `${cleanedFooter}${reportCreatedByHtml}`;
    await visitService.sendFollowUpLetter(visit.scheduledVisitID, {
      letterHeader: headerText,
      letterBody: letterBodyForSend,
      letterFooter: letterFooterForSend,
      piEmail: null,
      vRemarks: vRemarks || undefined,
      isDraft,
    });
    if (onGenerated) onGenerated();
    setToast({ type: 'success', message: commonMessages.authSuccess });
    if (closeAfter) onClose();
  };

  const validateLetterBody = () => {
    const text = getBodyPlainText(editorContent);
    if (!text) {
      setLetterBodyError(visitMessages.letterBodyEmpty);
      return false;
    }
    setLetterBodyError('');
    return true;
  };

  const handlePreview = () => {
    setLetterBodyError('');
    const text = getBodyPlainText(editorContent);
    if (!text) {
      setLetterBodyError(visitMessages.letterPreviewEmpty);
      return;
    }
    setShowPreview(true);
  };

  const handlePreviewDownloadPdf = async () => {
    const text = getBodyPlainText(editorContent);
    if (!text) {
      setToast({ type: 'error', message: visitMessages.letterPreviewEmpty });
      return;
    }
    const html = previewContentRef.current?.outerHTML ?? '';
    if (!html.trim()) {
      setToast({ type: 'error', message: visitMessages.letterPreviewEmpty });
      return;
    }
    const visitCode = formatVisitId(visit);
    setPdfDownloading(true);
    try {
      await downloadVisitLetterPdfFromHtml({
        html,
        fileName: `Follow-Up-Letter-${visitCode}.pdf`,
        title: 'Follow Up Letter',
      });
      setToast({ type: 'success', message: 'PDF downloaded successfully.' });
    } catch (err) {
      const message = await getVisitLetterPdfErrorMessage(err);
      setToast({ type: 'error', message: message || err?.message || 'PDF download failed' });
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!visit.scheduledVisitID) return;
    setLetterBodyError('');
    if (!validateLetterBody()) return;
    eSignRemarksRef.current = '';
    setShowESignModal(true);
  };

  const runAfterESign = () => {
    const vRemarks = eSignRemarksRef.current || '';
    setShowESignModal(false);
    eSignRemarksRef.current = '';
    setSaving(true);
    doSendLetter(vRemarks, { closeAfter: true, isDraft: false })
      .catch((err) => {
        console.error('Error generating follow-up letter:', err);
        const msg = err.response?.data?.message ?? err.response?.data?.error ?? err.message ?? 'Unable to create or update follow-up letter. Please try again.';
        setToast({ type: 'error', message: msg });
      })
      .finally(() => setSaving(false));
  };

  const handleSaveAndContinue = (e) => {
    e.preventDefault();
    if (!visit.scheduledVisitID) return;
    setLetterBodyError('');
    const text = getBodyPlainText(editorContent);
    if (!text) {
      setLetterBodyError(visitMessages.noDataToSave);
      return;
    }
    setSaving(true);
    doSendLetter('', { isDraft: true, closeAfter: true })
      .catch((err) => {
        console.error('Error saving follow-up letter draft:', err);
        const msg = err.response?.data?.message ?? err.response?.data?.error ?? err.message ?? 'Unable to save follow-up letter draft. Please try again.';
        setToast({ type: 'error', message: msg });
      })
      .finally(() => setSaving(false));
  };

  const handleClear = () => {
    setLetterBodyError('');
    bodyContentInitializedRef.current = false;
    setEditorContent('');
    setHistory(['']);
    setHistoryIndex(0);
    setStoredReportCreatedByHtml('');
    if (bodyEditorRef.current) bodyEditorRef.current.innerHTML = '';
  };

  const visitTypeLabel = VISIT_TYPES[visit.visitType] || visit.visitType || '';

  const renderOpenIssuesTable = () => (
    <div className="follow-up-open-issues-section">
      <h3 className="follow-up-open-issues-title">Issues</h3>
      <div className="table-responsive">
        <table className="follow-up-open-issues-table">
          <thead>
            <tr>
              <th>Reference No</th>
              <th>Site</th>
              <th>Visit Date</th>
              <th>Field Name</th>
              <th>Issue Description</th>
              <th>Expected Closure Date</th>
              <th>Priority</th>
              <th>Current Status</th>
            </tr>
          </thead>
          <tbody>
            {openIssuesLoading ? (
              <tr>
                <td colSpan="8" className="follow-up-open-issues-empty">Loading issues...</td>
              </tr>
            ) : openIssues.length > 0 ? (
              openIssues.map((item) => {
                const siteCell = [item.siteCode, item.siteDescription].filter(Boolean).join(' – ')
                  || visit.siteCode
                  || visit.SiteCode
                  || '-';
                const vd = item.visitDate ?? item.VisitDate ?? item.plannedStartDate;
                return (
                  <tr key={item.actionItemID ?? item.nCRFActionItemNo ?? item.actionItemNumber}>
                    <td>{item.actionItemNumber ?? item.vActionItemNumber ?? '-'}</td>
                    <td>{siteCell}</td>
                    <td>{vd ? formatDateDDMMMYYYY(vd) : '-'}</td>
                    <td>{item.actionItemOn || '-'}</td>
                    <td>{item.findingDescription || '-'}</td>
                    <td>{item.dueDate ? formatDateDDMMMYYYY(item.dueDate) : '-'}</td>
                    <td>{item.priorityDescription ?? item.severity ?? '-'}</td>
                    <td>{formatActionItemStatusForDisplay(item.status)}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="8" className="follow-up-open-issues-empty">No issues for this site.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const pushHistory = (content) => {
    setHistory(prev => {
      const trimmed = historyIndex >= 0 ? prev.slice(0, historyIndex + 1) : [];
      if (trimmed.length > 0 && trimmed[trimmed.length - 1] === content) return trimmed;
      const next = [...trimmed, content];
      setHistoryIndex(next.length - 1);
      return next;
    });
  };

  const applyCommand = (command, value = null) => {
    if (!bodyEditorRef.current) return;
    bodyEditorRef.current.focus();
    document.execCommand(command, false, value);
    const html = bodyEditorRef.current.innerHTML;
    setEditorContent(html);
    pushHistory(html);
  };

  const handleBodyInput = (e) => {
    const html = e.currentTarget.innerHTML;
    setEditorContent(html);
    setLetterBodyError('');
    pushHistory(html);
  };

  const handleUndoClick = () => {
    if (historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    const html = history[newIndex];
    setEditorContent(html);
    if (bodyEditorRef.current) bodyEditorRef.current.innerHTML = html;
  };

  const handleRedoClick = () => {
    if (historyIndex === -1 || historyIndex >= history.length - 1) return;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    const html = history[newIndex];
    setEditorContent(html);
    if (bodyEditorRef.current) bodyEditorRef.current.innerHTML = html;
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      title={isViewMode ? 'View Follow-Up Letter' : 'Generate Follow-Up Letter'}
      className="generate-letter-modal"
    >
      <form onSubmit={(e) => { if (isViewMode) { e.preventDefault(); return; } handleSubmit(e); }}>
        <VisitModalHeader
          projectCode={visit.projectCode}
          siteName={visit.siteName}
          siteCode={visit.siteCode ?? visit.SiteCode}
          piName={resolvedPiName ?? visit.piName ?? visit.PIName ?? visit.pIName}
        />

        <div className="form-group">
          <label className="form-label">Visit Type</label>
          <input type="text" className="form-input" value={visitTypeLabel} disabled />
        </div>

        <div className="form-group generate-letter-header-block">
          <textarea className="form-textarea generate-letter-template" rows="6" value={headerText} readOnly />
        </div>

        <div className="form-group">
          {!isViewMode && (
            <div className="richtext-toolbar">
              <button type="button" className="rt-btn" onClick={() => applyCommand('bold')}>B</button>
              <button type="button" className="rt-btn" onClick={() => applyCommand('italic')}>I</button>
              <button type="button" className="rt-btn" onClick={() => applyCommand('underline')}>U</button>
              <button type="button" className="rt-btn" onClick={() => applyCommand('insertUnorderedList')}>•</button>
              <button type="button" className="rt-btn" onClick={() => applyCommand('insertOrderedList')}>1.</button>
              <button type="button" className="rt-btn" onClick={() => applyCommand('justifyLeft')}>L</button>
              <button type="button" className="rt-btn" onClick={() => applyCommand('justifyCenter')}>C</button>
              <button type="button" className="rt-btn" onClick={() => applyCommand('justifyRight')}>R</button>
              <button type="button" className="rt-btn" onClick={handleUndoClick}>↺</button>
              <button type="button" className="rt-btn" onClick={handleRedoClick}>↻</button>
              <div className="rt-select-wrap">
                <span className="rt-select-label">{blockFormat === 'h1' ? 'Heading 1' : blockFormat === 'h2' ? 'Heading 2' : blockFormat === 'h3' ? 'Heading 3' : 'Normal'}</span>
                <select
                  className="rt-select"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) return;
                    setBlockFormat(value);
                    applyCommand('formatBlock', value);
                  }}
                  value={blockFormat}
                  aria-label="Block format"
                >
                  <option value="p">Normal</option>
                  <option value="h1">Heading 1</option>
                  <option value="h2">Heading 2</option>
                  <option value="h3">Heading 3</option>
                </select>
              </div>
              <div className="rt-select-wrap rt-select-wrap--font">
                <span className="rt-select-label">{fontFamily || 'Arial'}</span>
                <select
                  className="rt-select"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) return;
                    applyCommand('fontName', value);
                    setFontFamily(value);
                  }}
                  value={fontFamily}
                  aria-label="Font family"
                >
                  <option value="">Font Family</option>
                  <option value="Arial">Arial</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Calibri">Calibri</option>
                  <option value="Georgia">Georgia</option>
                </select>
              </div>
            </div>
          )}

          <div
            ref={bodyEditorRef}
            className="form-textarea richtext-editor"
            suppressContentEditableWarning
            onInput={isViewMode ? undefined : handleBodyInput}
            contentEditable={!isViewMode}
          />
          {letterBodyError && (
            <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{letterBodyError}</div>
          )}
        </div>

        {renderOpenIssuesTable()}

        <div className="form-group generate-letter-footer-block">
          <textarea className="form-textarea generate-letter-template" rows="4" value={footerText} readOnly />
        </div>

        <div className="generate-letter-footer">
          <button type="button" className="btn btn-primary" onClick={handlePreview} disabled={saving}>Preview</button>
          {!isViewMode && (
            <>
              <button type="button" className="btn btn-primary" onClick={handleSaveAndContinue} disabled={saving}>
                {saving ? 'Saving...' : 'Draft Save'}
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Generating...' : 'Submit'}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={handleClear} disabled={saving}>Clear</button>
            </>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </form>
    </Modal>

    <Modal
      isOpen={showPreview}
      onClose={() => setShowPreview(false)}
      size="large"
      title="Generate Follow-Up Letter Preview"
      className="generate-letter-preview-modal"
      footer={
        <>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handlePreviewDownloadPdf}
            disabled={pdfDownloading || openIssuesLoading}
          >
            {pdfDownloading ? 'Downloading…' : 'Download'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setShowPreview(false)}>Close</button>
        </>
      }
    >
      <div ref={previewContentRef} className="generate-letter-preview-content">
        {headerText && <pre className="generate-letter-preview-header">{headerText}</pre>}
        <div className="generate-letter-preview-body" dangerouslySetInnerHTML={{ __html: editorContent }} />
        {renderOpenIssuesTable()}
        {footerText && <pre className="generate-letter-preview-footer">{footerText}</pre>}
        {storedReportCreatedByHtml ? (
          <div dangerouslySetInnerHTML={{ __html: storedReportCreatedByHtml }} />
        ) : (() => {
          let signerName, signerRole, datePart, timePart;
          if (backendCreatorData) {
            const parsed = parseVRecordedSign(backendCreatorData.sign);
            signerName = parsed.name || '\u00A0';
            signerRole = parsed.role || '\u00A0';
            const dtParts = formatUtcWithOffsetToDateTimeParts(backendCreatorData.utc, backendCreatorData.offset);
            datePart = dtParts ? dtParts.datePart : '';
            timePart = dtParts ? dtParts.timePart : '';
          } else {
            signerName = getSignerFullName(authUser) || '\u00A0';
            signerRole = getSignerRole(authUser) || '\u00A0';
            const dt = formatDateTimeWithSeconds();
            datePart = dt.datePart;
            timePart = dt.timePart;
          }
          return (
            <div className="letter-report-created-section">
              <table className="letter-report-created-table">
                <thead>
                  <tr><th colSpan="4" className="letter-report-created-header">Letter Generated By</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="letter-report-created-label">Signature</td>
                    <td>{signerName}</td>
                    <td className="letter-report-created-label">Role in the Study</td>
                    <td>{signerRole}</td>
                  </tr>
                  <tr>
                    <td className="letter-report-created-label">Name</td>
                    <td>{signerName}</td>
                    <td className="letter-report-created-label">Date</td>
                    <td>{datePart} {timePart}</td>
                  </tr>
                </tbody>
              </table>
              <p className="letter-report-created-note">
                <strong>Note:</strong> This is electronically signed document and manual signature is not required.
              </p>
            </div>
          );
        })()}
      </div>
    </Modal>

    <ElectronicSign
      show={showESignModal}
      onClose={() => setShowESignModal(false)}
      onAuthSuccess={(vRemarks) => { eSignRemarksRef.current = vRemarks || ''; }}
      onSave={runAfterESign}
    />

    {toast && (
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
        <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      </div>
    )}
    </>
  );
};

export default GenerateFollowUpLetterModal;

